/**
 * @fileoverview Audit Log API service.
 * @description Searches audit events (GET /audit/events, one page at a time, with the scope the
 * backend actually searched in `meta.searchScope`) and reads one event (GET
 * /audit/events/{auditEventId}). Read-only: audit events are written by backend modules, never
 * over HTTP. Both routes are rate-limited and every successful call is itself audited by the
 * backend.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { httpClient } from '@/shared/api/http-client'
import { toPaginated } from '@/shared/api/pagination.utils'

import type {
  AuditEventDetail,
  AuditEventsPage,
  AuditEventsParams,
  AuditEventSummary,
  AuditSearchMeta,
} from '@/features/audit/types/audit.types'
import type { ResponseWithPageMeta } from '@/shared/api/pagination.utils'

/**
 * @description Fetches one page of audit event summaries in the caller's authorized scope.
 * @param {AuditEventsParams} params - The required date range, optional filters, sort and page.
 * @returns {Promise<AuditEventsPage>} The page, its position and the scope that was searched.
 */
export async function getAuditEventsPage(params: AuditEventsParams): Promise<AuditEventsPage> {
  const response = await httpClient.get<AuditEventSummary[]>('/audit/events', { params })
  const { items, meta } = toPaginated(response)
  // The shared interceptor keeps the envelope's whole `meta`, which here also carries searchScope.
  const searchMeta = (response as ResponseWithPageMeta & { pageMeta?: AuditSearchMeta }).pageMeta

  return { items, meta: searchMeta ?? meta }
}

/**
 * @description Fetches one audit event's detail.
 * @param {string} auditEventId - The event's id.
 * @returns {Promise<AuditEventDetail>} The event, masked unless the caller may see sensitive data.
 */
export async function getAuditEvent(auditEventId: string): Promise<AuditEventDetail> {
  const { data } = await httpClient.get<AuditEventDetail>(
    `/audit/events/${encodeURIComponent(auditEventId)}`,
  )
  return data
}
